import React, { useEffect, useState } from 'react';
import { invoiceService, commentService } from '../services/api';
import { Invoice, Comment } from '../types';
import { useAuth } from '../context/AuthContext';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Search, Filter, RefreshCw, ChevronDown, ChevronUp, Circle, FileText, DollarSign, RotateCcw, XCircle, X, Send, Edit2, Save, Trash2, MessageSquare, Building2, Calendar, User, CheckCircle } from 'lucide-react';

const InvoiceList: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedGestion, setExpandedGestion] = useState<number[]>([]);
  const [activeTab, setActiveTab] = useState<'invoices' | 'completed' | 'refacturar' | 'paid' | 'denied'>('invoices');
  
  // Modal states
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [clientGestion, setClientGestion] = useState('');
  const [isAddingGestion, setIsAddingGestion] = useState(false);
  const [isSavingGestion, setIsSavingGestion] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<Invoice>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchInvoices = async () => {
    setIsLoading(true);
    try {
      const response = await invoiceService.getAll();
      setInvoices(response.data);
      setFilteredInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  // Auto-open invoice from URL param
  useEffect(() => {
    const invoiceId = searchParams.get('invoice');
    if (invoiceId && invoices.length > 0) {
      const inv = invoices.find(i => i.id === parseInt(invoiceId));
      if (inv) {
        openInvoiceDetail(inv);
        setSearchParams({}, { replace: true });
      }
    }
  }, [invoices, searchParams]);

  useEffect(() => {
    let result = invoices;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(inv => 
        inv.invoice_number.toLowerCase().includes(term) ||
        inv.pending_notes?.toLowerCase().includes(term) ||
        inv.plan_code?.toLowerCase().includes(term)
      );
    }

    if (statusFilter !== 'all') {
      result = result.filter(inv => inv.status === statusFilter);
    }

    setFilteredInvoices(result);
  }, [searchTerm, statusFilter, invoices]);

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      in_process: 'bg-orange-100 text-orange-700 border-orange-300',
      completed: 'bg-green-100 text-green-700 border-green-300',
      denied: 'bg-red-100 text-red-700 border-red-300',
      paid: 'bg-purple-100 text-purple-700 border-purple-300',
      refacturar: 'bg-orange-100 text-orange-700 border-orange-300',
    };

    const labels: Record<string, string> = {
      pending: 'Pendiente',
      in_process: 'En Proceso',
      completed: 'Completada',
      denied: 'Denegada',
      paid: 'Pagada',
      refacturar: 'refacturar',
    };

    return (
      <span className={`px-2 py-1 text-xs rounded-full font-medium border ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getResultStyle = (color: string) => {
    if (!color || color === '#FFFFFF') return {};
    return { backgroundColor: color };
  };

  const openInvoiceDetail = async (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowDetailModal(true);
    try {
      const commentsRes = await commentService.getByInvoice(invoice.id);
      setComments(commentsRes.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedInvoice(null);
    setComments([]);
    setNewComment('');
    setClientGestion('');
    setIsAddingGestion(false);
    setIsEditing(false);
    setEditData({});
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedInvoice) return;
    try {
      const response = await commentService.create({
        invoiceId: selectedInvoice.id,
        comment: newComment
      });
      setComments(prev => [response.data, ...prev]);
      setNewComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleSaveGestion = async () => {
    if (!selectedInvoice) return;
    setIsSavingGestion(true);
    try {
      const now = new Date();
      const dateTime = `${now.toLocaleDateString('en-US')} ${now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
      const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
      const newEntry = `[${dateTime} - ${userName}]: ${clientGestion}`;
      const newGestion = selectedInvoice.management_notes 
        ? `${selectedInvoice.management_notes}\n\n${newEntry}`
        : newEntry;
      
      // If client adds a note and invoice is pending, auto-change to in_process
      const autoStatus: Invoice['status'] | undefined = (user?.role === 'client' && selectedInvoice.status === 'pending') ? 'in_process' : undefined;
      await invoiceService.update(selectedInvoice.id, { 
        managementNotes: newGestion,
        ...(autoStatus && { status: autoStatus })
      });
      const updatedInvoice: Invoice = { ...selectedInvoice, management_notes: newGestion, ...(autoStatus && { status: autoStatus }) };
      setSelectedInvoice(updatedInvoice);
      setInvoices(prev => prev.map(inv => inv.id === selectedInvoice.id ? updatedInvoice : inv));
      setClientGestion('');
      setIsAddingGestion(false);
    } catch (error) {
      console.error('Error saving gestion:', error);
    } finally {
      setIsSavingGestion(false);
    }
  };

  const handleStatusChange = async (newStatus: Invoice['status']) => {
    if (!selectedInvoice) return;
    try {
      await invoiceService.update(selectedInvoice.id, { status: newStatus });
      const updatedInvoice = { ...selectedInvoice, status: newStatus };
      setSelectedInvoice(updatedInvoice);
      setInvoices(prev => prev.map(inv => inv.id === selectedInvoice.id ? updatedInvoice : inv));
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const startEditing = () => {
    if (selectedInvoice) {
      setEditData({
        pending_notes: selectedInvoice.pending_notes,
        management_notes: selectedInvoice.management_notes,
        status: selectedInvoice.status
      });
      setIsEditing(true);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedInvoice) return;
    setIsSaving(true);
    try {
      await invoiceService.update(selectedInvoice.id, editData);
      const updatedInvoice = { ...selectedInvoice, ...editData };
      setSelectedInvoice(updatedInvoice);
      setInvoices(prev => prev.map(inv => inv.id === selectedInvoice.id ? updatedInvoice : inv));
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving invoice:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedInvoice) return;
    if (!window.confirm(`¿Eliminar factura #${selectedInvoice.invoice_number}?`)) return;
    setIsDeleting(true);
    try {
      await invoiceService.delete(selectedInvoice.id);
      setInvoices(prev => prev.filter(inv => inv.id !== selectedInvoice.id));
      closeDetailModal();
    } catch (error) {
      console.error('Error deleting invoice:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const statusLabels: Record<string, string> = {
    pending: 'Pendiente',
    in_process: 'En Proceso',
    completed: 'Completada',
    denied: 'Denegada',
    paid: 'Pagada',
    refacturar: 'Re-Facturar',
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_process: 'bg-orange-100 text-orange-800',
      completed: 'bg-green-100 text-green-800',
      denied: 'bg-red-100 text-red-800',
      paid: 'bg-purple-100 text-purple-800',
      refacturar: 'bg-orange-100 text-orange-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Listado de Facturas</h1>
          <p className="text-gray-500">Facturas con pendientes para corregir</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchInvoices}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition"
          >
            <RefreshCw size={18} />
            Actualizar
          </button>
          {user?.role === 'admin' && (
            <Link
              to="/invoices/new"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
            >
              <Plus size={18} />
              Nueva Factura
            </Link>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setActiveTab('invoices')}
              className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition flex items-center justify-center gap-2 ${
                activeTab === 'invoices'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <FileText size={16} />
              Facturas ({invoices.filter(inv => !['paid', 'refacturar', 'denied', 'completed'].includes(inv.status)).length})
            </button>
            <button
              onClick={() => setActiveTab('refacturar')}
              className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition flex items-center justify-center gap-2 ${
                activeTab === 'refacturar'
                  ? 'border-orange-600 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <RotateCcw size={16} />
              Re-Facturar ({invoices.filter(inv => inv.status === 'refacturar').length})
            </button>
            <button
              onClick={() => setActiveTab('paid')}
              className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition flex items-center justify-center gap-2 ${
                activeTab === 'paid'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <DollarSign size={16} />
              Pagadas ({invoices.filter(inv => inv.status === 'paid').length})
            </button>
            <button
              onClick={() => setActiveTab('denied')}
              className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition flex items-center justify-center gap-2 ${
                activeTab === 'denied'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <XCircle size={16} />
              Denegadas ({invoices.filter(inv => inv.status === 'denied').length})
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition flex items-center justify-center gap-2 ${
                activeTab === 'completed'
                  ? 'border-green-600 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <CheckCircle size={16} />
              Completadas ({invoices.filter(inv => inv.status === 'completed').length})
            </button>
          </div>
        </div>

        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por número, plan o notas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={20} className="text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todos los estados</option>
              <option value="pending">Pendiente</option>
              <option value="in_process">En Proceso</option>
              <option value="completed">Completada</option>
              <option value="denied">Denegada</option>
              <option value="paid">Pagada</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Factura</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pendiente</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gestión</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filteredInvoices
                .filter(inv => {
                  if (activeTab === 'invoices') return !['paid', 'refacturar', 'denied', 'completed'].includes(inv.status);
                  if (activeTab === 'completed') return inv.status === 'completed';
                  if (activeTab === 'refacturar') return inv.status === 'refacturar';
                  if (activeTab === 'paid') return inv.status === 'paid';
                  if (activeTab === 'denied') return inv.status === 'denied';
                  return true;
                })
                .map((invoice) => {
                const statusColors: Record<string, string> = {
                  pending: '#F59E0B',
                  in_process: '#3B82F6',
                  completed: '#10B981',
                  denied: '#EF4444',
                  paid: '#8B5CF6',
                };
                return (
                  <tr key={invoice.id} onClick={() => openInvoiceDetail(invoice)} className="hover:bg-blue-50/50 transition cursor-pointer">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Circle size={10} fill={statusColors[invoice.status] || '#9CA3AF'} stroke="none" />
                        <span className="text-blue-600 font-medium">
                          {invoice.company_name || `${invoice.client_first_name || ''} ${invoice.client_last_name || ''}`.trim() || '-'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      <span className="font-medium text-gray-800">
                        #{invoice.invoice_number}
                      </span>
                      <span className="text-gray-400 ml-2 text-xs">
                        {new Date(invoice.invoice_date).toLocaleDateString('en-US')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(invoice.status)}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {invoice.plan_code || '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-xs">
                      <div className="line-clamp-2 text-xs">{invoice.pending_notes || '-'}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-sm">
                      {invoice.management_notes ? (
                        <div>
                          {expandedGestion.includes(invoice.id) ? (
                            <div className="whitespace-pre-wrap text-xs space-y-2">
                              {invoice.management_notes.split('\n\n').map((entry, idx) => (
                                <div key={idx} className="p-2 bg-gray-50 rounded border-l-2 border-blue-400">
                                  {entry}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="line-clamp-2 whitespace-pre-wrap text-xs">
                              {invoice.management_notes.split('\n\n').slice(-1)[0]}
                            </div>
                          )}
                          {invoice.management_notes.split('\n\n').length > 1 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedGestion(prev => 
                                  prev.includes(invoice.id) 
                                    ? prev.filter(id => id !== invoice.id)
                                    : [...prev, invoice.id]
                                );
                              }}
                              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium mt-1"
                            >
                              {expandedGestion.includes(invoice.id) ? (
                                <>
                                  <ChevronUp size={14} />
                                  Ocultar
                                </>
                              ) : (
                                <>
                                  <ChevronDown size={14} />
                                  +{invoice.management_notes.split('\n\n').length - 1} más
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      ) : '-'}
                    </td>
                  </tr>
                );
              })}
              {filteredInvoices.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    No se encontraron facturas
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t border-gray-200 text-sm text-gray-500">
          Mostrando {filteredInvoices.filter(inv => {
            if (activeTab === 'invoices') return !['paid', 'refacturar', 'denied', 'completed'].includes(inv.status);
            if (activeTab === 'completed') return inv.status === 'completed';
            if (activeTab === 'refacturar') return inv.status === 'refacturar';
            if (activeTab === 'paid') return inv.status === 'paid';
            if (activeTab === 'denied') return inv.status === 'denied';
            return true;
          }).length} de {invoices.length} facturas
        </div>
      </div>

      {/* Invoice Detail Modal */}
      {showDetailModal && selectedInvoice && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden border border-gray-200/50">
            {/* Header */}
            <div className="relative px-8 py-6 bg-white border-b border-gray-100">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-5">
                  <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-600/20">
                    <FileText size={28} className="text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3">
                      <h2 className="text-xl font-bold text-gray-900">
                        #{selectedInvoice.invoice_number}
                      </h2>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(selectedInvoice.status)}`}>
                        {statusLabels[selectedInvoice.status]}
                      </span>
                    </div>
                    <p className="text-gray-500 mt-1">
                      {selectedInvoice.company_name || `${selectedInvoice.client_first_name} ${selectedInvoice.client_last_name}`}
                    </p>
                  </div>
                </div>
                <button onClick={closeDetailModal} className="p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all">
                  <X size={22} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex flex-col lg:flex-row max-h-[calc(90vh-100px)]">
              {/* Left Panel - Main Content */}
              <div className="flex-1 p-8 overflow-y-auto bg-gray-50/50">
                {/* Key Info Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FileText size={16} className="text-blue-600" />
                      </div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</p>
                    </div>
                    <p className="text-base font-bold text-gray-900">{selectedInvoice.plan_code || '-'}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                        <DollarSign size={16} className="text-green-600" />
                      </div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Monto</p>
                    </div>
                    <p className="text-base font-bold text-gray-900">${Number(selectedInvoice.amount || 0).toFixed(2)}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Calendar size={16} className="text-purple-600" />
                      </div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Factura</p>
                    </div>
                    <p className="text-base font-bold text-gray-900">{new Date(selectedInvoice.invoice_date).toLocaleDateString('en-US')}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Calendar size={16} className="text-orange-600" />
                      </div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Servicio</p>
                    </div>
                    <p className="text-base font-bold text-gray-900">{selectedInvoice.service_date ? new Date(selectedInvoice.service_date).toLocaleDateString('en-US') : '-'}</p>
                  </div>
                </div>

                {/* Pendiente Section */}
                {selectedInvoice.pending_notes && (
                  <div className="mb-8 p-5 bg-amber-50 border-l-4 border-amber-400 rounded-r-xl shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-6 h-6 bg-amber-400 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">!</span>
                      </div>
                      <h4 className="text-sm font-bold text-amber-800 uppercase tracking-wide">Pendiente</h4>
                    </div>
                    <p className="text-amber-900 leading-relaxed">{selectedInvoice.pending_notes}</p>
                  </div>
                )}

                {/* Notas de Gestión */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50/50">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <MessageSquare size={18} className="text-blue-600" />
                      Notas de Gestión
                    </h3>
                    {!isAddingGestion && (
                      <button
                        onClick={() => setIsAddingGestion(true)}
                        className="text-sm text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                      >
                        <Plus size={16} />
                        Agregar Nota
                      </button>
                    )}
                  </div>

                  <div className="p-5">
                    {/* Add Note Form */}
                    {isAddingGestion && (
                      <div className="mb-5 p-5 bg-blue-50/50 border border-blue-100 rounded-xl">
                        <textarea
                          value={clientGestion}
                          onChange={(e) => setClientGestion(e.target.value)}
                          placeholder="Escriba su nota de gestión aquí..."
                          className="w-full px-4 py-3 border border-blue-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none shadow-sm"
                          rows={3}
                          autoFocus
                        />
                        <div className="flex gap-3 justify-end mt-4">
                          <button
                            onClick={() => { setIsAddingGestion(false); setClientGestion(''); }}
                            className="px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            Cancelar
                          </button>
                          <button
                            onClick={handleSaveGestion}
                            disabled={!clientGestion.trim() || isSavingGestion}
                            className="px-5 py-2.5 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 shadow-sm"
                          >
                            {isSavingGestion ? 'Guardando...' : 'Guardar Nota'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Notes List */}
                    <div className="space-y-4 max-h-64 overflow-y-auto pr-2">
                      {selectedInvoice.management_notes ? (
                        selectedInvoice.management_notes.split('\n\n').map((entry, idx) => {
                          const match = entry.match(/\[(.+?) - (.+?)\]: ([\s\S]*)/);
                          if (match) {
                            const [, dateTime, userName, content] = match;
                            return (
                              <div key={idx} className="flex gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100 hover:bg-gray-100/50 transition-colors">
                                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                                  <span className="text-white font-bold text-sm">{userName.charAt(0).toUpperCase()}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-3 mb-2">
                                    <span className="font-semibold text-gray-900 text-sm">{userName}</span>
                                    <span className="text-xs text-gray-400 bg-gray-200 px-2 py-0.5 rounded-full">{dateTime}</span>
                                  </div>
                                  <p className="text-gray-700 leading-relaxed text-sm">{content}</p>
                                </div>
                              </div>
                            );
                          }
                          return (
                            <div key={idx} className="p-4 bg-gray-50 rounded-xl text-gray-700 border border-gray-100">
                              {entry}
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-center py-12 text-gray-400">
                          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <MessageSquare size={28} className="text-gray-300" />
                          </div>
                          <p className="font-medium">Sin notas de gestión</p>
                          <p className="text-sm mt-1">Las notas aparecerán aquí</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Panel - Actions */}
              {user?.role === 'admin' && (
                <div className="w-full lg:w-72 p-6 bg-white border-t lg:border-t-0 lg:border-l border-gray-100">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-5">Panel de Control</h4>

                  {/* Change Status */}
                  <div className="mb-6">
                    <label className="text-sm font-medium text-gray-700 block mb-2">Estado de Factura</label>
                    <select
                      value={selectedInvoice.status}
                      onChange={(e) => handleStatusChange(e.target.value as Invoice['status'])}
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-colors cursor-pointer font-medium"
                    >
                      <option value="pending">Pendiente</option>
                      <option value="in_process">En Proceso</option>
                      <option value="completed">Completada</option>
                      <option value="denied">Denegada</option>
                      <option value="refacturar">Re-Facturar</option>
                      <option value="paid">Pagada</option>
                    </select>
                  </div>

                  {/* Actions */}
                  <div className="space-y-3">
                    <button
                      onClick={startEditing}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold shadow-sm shadow-blue-600/20 transition-all"
                    >
                      <Edit2 size={16} />
                      Editar Factura
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
                    >
                      <Trash2 size={16} />
                      Eliminar
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditing && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-800">Editar Factura</h2>
              <button onClick={() => setIsEditing(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pendiente</label>
                <textarea
                  value={editData.pending_notes || ''}
                  onChange={(e) => setEditData({ ...editData, pending_notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                <select
                  value={editData.status || ''}
                  onChange={(e) => setEditData({ ...editData, status: e.target.value as Invoice['status'] })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="pending">Pendiente</option>
                  <option value="in_process">En Proceso</option>
                  <option value="completed">Completada</option>
                  <option value="denied">Denegada</option>
                  <option value="refacturar">Re-Facturar</option>
                  <option value="paid">Pagada</option>
                </select>
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
                >
                  {isSaving ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save size={16} />
                  )}
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceList;
